/**
 * **판매처로 나갔다는 표시** (v21/F13).
 *
 * ── 왜 필요한가 ─────────────────────────────────────────────
 *
 * 설치된 앱(홈 화면 PWA · Android WebAPK)에서는 **외부로 나가는 모든 방법이 앱을 그
 * 자리에서 대체한다.** 세 방식을 실기로 재 봤고 셋 다 그랬다 —
 * 셸 브리지 없는 `window.open`(창을 주는 척하며 대체한다) · `<a target="_blank">` ·
 * 같은 탭 이동. `params.exit` 이 `new_tab` 이든 `handed_off` 든 **1초 안에 앱이 다시
 * 떴다**(`docs/46` §3.9).
 *
 * **그래서 게이트는 없앨 수 없다.** 소리 있는 재생은 제스처 한 번을 요구하고
 * (`StartGate` 의 존재 이유 전부), 다시 뜬 앱은 그것을 다시 물어야 한다.
 *
 * 없앨 수 없는 것과 **오해를 주는 것은 다르다.** 채널과 지점은 이미 살아 있다 —
 * 채널은 `readSavedChannel()`, 지점은 `WatchProgress`(계정 축, 일반 채널)다. 그러니 그
 * 게이트는 「처음 보기」가 아니라 **「이어서 보기」**여야 한다. 이 표시가 그 한 글자를 가른다.
 *
 * ── 왜 `localStorage` 인가 ──────────────────────────────────
 *
 * 나갔다 오는 것은 **새 페이지 적재**다 — 탭이 대체됐으므로 `sessionStorage` 는 남는다는
 * 보장이 없다. 대신 **신선도 창**을 둔다: 오래된 표시가 며칠 뒤의 게이트 문구를 바꾸면
 * 그것도 거짓말이다.
 */
const KEY = 'bloom.leftForCheckout';

/**
 * 이 창 안에서 돌아온 것만 「이어서」로 본다.
 *
 * 5분이면 판매처를 보고 돌아오는 데 넉넉하고, 그보다 오래면 시청자에게도 그 이탈은
 * 이미 다른 일이다. **길게 잡으면 무관한 재실행이 「이어서」로 보인다.**
 */
const FRESH_MS = 5 * 60_000;

/** 떠나기 직전에 남긴다. 저장소가 막혀 있으면(사파리 프라이빗) 조용히 아무 일도 없다. */
export function markLeftForCheckout(nowMs: number = Date.now()): void {
  try {
    localStorage.setItem(KEY, String(nowMs));
  } catch {
    // 표시를 못 남기면 게이트가 첫 진입 문구로 뜬다 — 그것은 **틀린 말이 아니다.**
  }
}

/**
 * 돌아왔는가. **한 번만 참이다** — 읽으면서 지운다.
 *
 * 지우지 않으면 그 뒤의 모든 게이트가 「이어서」가 되고, 진짜 첫 진입에도 그 문구가 뜬다.
 */
export function takeLeftForCheckout(nowMs: number = Date.now()): boolean {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return false;
    localStorage.removeItem(KEY);
    const at = Number(raw);
    return Number.isFinite(at) && nowMs - at >= 0 && nowMs - at <= FRESH_MS;
  } catch {
    return false;
  }
}
